import { createClient } from '@supabase/supabase-js';

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY;

// Create Supabase client
const supabase = createClient(SUPABASE_URL, SUPABASE_ANON_KEY);

// Cache configuration
const CACHE_CONFIG = {
  PRODUCTS_LIGHT: {
    key: 'products_light_cache',
    ttl: 5 * 60 * 1000, // 5 minutes for lightweight data
    version: 'v1'
  },
  PRODUCTS_FULL: {
    key: 'products_full_cache', 
    ttl: 10 * 60 * 1000, // 10 minutes for full data
    version: 'v1'
  },
  SINGLE_PRODUCT: {
    key: 'single_product_cache',
    ttl: 15 * 60 * 1000, // 15 minutes for individual products
    version: 'v1'
  }
};

interface CacheItem<T> {
  data: T;
  timestamp: number;
  version: string;
  etag?: string;
}

// Cache utilities
function getCacheKey(baseKey: string, identifier?: string): string {
  return identifier ? `${baseKey}_${identifier}` : baseKey;
}

function getFromCache<T>(cacheKey: string, ttl: number, version: string): T | null {
  try {
    const cached = localStorage.getItem(cacheKey);
    if (!cached) return null;

    const cacheItem: CacheItem<T> = JSON.parse(cached);
    const now = Date.now();
    
    // Check version compatibility
    if (cacheItem.version !== version) {
      localStorage.removeItem(cacheKey);
      return null;
    }
    
    // Check if cache is still valid
    if (now - cacheItem.timestamp > ttl) {
      localStorage.removeItem(cacheKey);
      return null;
    }

    return cacheItem.data;
  } catch (error) {
    console.warn('Cache read error:', error);
    return null;
  }
}

function setCache<T>(cacheKey: string, data: T, version: string, etag?: string): void {
  try {
    const cacheItem: CacheItem<T> = {
      data,
      timestamp: Date.now(),
      version,
      etag
    };
    localStorage.setItem(cacheKey, JSON.stringify(cacheItem));
  } catch (error) {
    console.warn('Cache write error:', error);
    // If localStorage is full, clear old cache entries
    if (error instanceof DOMException && error.code === 22) {
      clearOldCache();
      try {
        localStorage.setItem(cacheKey, JSON.stringify({ data, timestamp: Date.now(), version, etag }));
      } catch (retryError) {
        console.warn('Cache write retry failed:', retryError);
      }
    }
  }
}

function clearOldCache(): void {
  const now = Date.now();
  const keysToRemove: string[] = [];
  
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && (key.includes('products_') || key.includes('single_product_'))) {
      try {
        const cached = localStorage.getItem(key);
        if (cached) {
          const cacheItem = JSON.parse(cached);
          // Remove items older than 1 hour
          if (now - cacheItem.timestamp > 60 * 60 * 1000) {
            keysToRemove.push(key);
          }
        }
      } catch (error) {
        keysToRemove.push(key);
      }
    }
  }
  
  keysToRemove.forEach(key => localStorage.removeItem(key));
}

// Lightweight products for grid view (minimal data)
export async function fetchProductsLight() {
  // Check if environment variables are properly loaded
  if (!SUPABASE_URL || !SUPABASE_ANON_KEY) {
    throw new Error('Supabase configuration missing. Please ensure VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY are set in environment variables.');
  }

  const config = CACHE_CONFIG.PRODUCTS_LIGHT;
  const cacheKey = getCacheKey(config.key);
  
  // Try cache first
  const cached = getFromCache(cacheKey, config.ttl, config.version);
  if (cached) {
    console.log('Using cached lightweight products');
    return cached;
  }

  try {
    console.log('Fetching lightweight products from server...');
    
    const response = await fetch(`${SUPABASE_URL}/functions/v1/get-products-light`, {
      headers: {
        'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
        'Content-Type': 'application/json',
      },
      method: 'GET',
    });
    
    if (!response.ok) {
      throw new Error(`Failed to fetch lightweight products: ${response.status}`);
    }
    
    const data = await response.json();
    
    // Cache the result
    setCache(cacheKey, data, config.version);
    
    return data;
  } catch (error) {
    console.error('Error fetching lightweight products:', error);
    
    // Fallback to full products if lightweight fails
    try {
      const fallbackData = await fetchProducts();
      // Transform to lightweight format
      const lightweightData = transformToLightweight(fallbackData);
      setCache(cacheKey, lightweightData, config.version);
      return lightweightData;
    } catch (fallbackError) {
      console.error('Fallback also failed:', fallbackError);
      throw new Error('Unable to fetch products');
    }
  }
}

// Transform full product data to lightweight format
function transformToLightweight(products: any[]) {
  return products.map(product => ({
    id: product.id,
    class: product.class,
    category: product.category,
    brand: product.brand,
    variants: product.variants.map((variant: any) => ({
      id: variant.id,
      name: variant.name,
      color: variant.color,
      color_code: variant.color_code,
      price: variant.price,
      discounted_price: variant.discounted_price,
      stock: variant.stock,
      new: variant.new,
      images: variant.images ? variant.images.slice(0, 2) : [],
      review_count: variant.review_count || 0
    }))
  }));
}

// Full products with all data
export async function fetchProducts() {
  // Check if environment variables are properly loaded
  if (!SUPABASE_URL || !SUPABASE_ANON_KEY) {
    throw new Error('Supabase configuration missing. Please ensure VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY are set in environment variables.');
  }

  const config = CACHE_CONFIG.PRODUCTS_FULL;
  const cacheKey = getCacheKey(config.key);
  
  // Try cache first
  const cached = getFromCache(cacheKey, config.ttl, config.version);
  if (cached) {
    console.log('Using cached full products');
    return cached;
  }

  try {
    console.log('Fetching full products from server...');
    
    // First, try to get the session to use authenticated requests
    const { data: { session } } = await supabase.auth.getSession();
    
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    // Use session token if available, otherwise use anon key
    if (session?.access_token) {
      headers['Authorization'] = `Bearer ${session.access_token}`;
    } else {
      headers['Authorization'] = `Bearer ${SUPABASE_ANON_KEY}`;
    }

    const response = await fetch(`${SUPABASE_URL}/functions/v1/get-products`, {
      headers,
      method: 'GET',
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to fetch products from Supabase: ${response.status} ${response.statusText} - ${errorText}`);
    }
    
    const data = await response.json();
    
    // Cache the result
    setCache(cacheKey, data, config.version);
    
    return data;
  } catch (error) {
    console.error('Error fetching products from Supabase:', error);
    
    // Fallback: try to fetch directly from the database if edge function fails
    try {
      console.log('Attempting fallback: fetching products directly from database...');
      const { data: products, error: dbError } = await supabase
        .from('products')
        .select(`
          *,
          product_variants (*)
        `);
      
      if (dbError) {
        throw dbError;
      }
      
      // Cache the fallback data too
      setCache(cacheKey, products, config.version);
      
      return products;
    } catch (fallbackError) {
      console.error('Fallback also failed:', fallbackError);
      throw new Error('Unable to fetch products from both Edge Function and direct database access');
    }
  }
}

// Single product fetch with caching
export async function fetchSingleProduct(variantId: string) {
  const config = CACHE_CONFIG.SINGLE_PRODUCT;
  const cacheKey = getCacheKey(config.key, variantId);
  
  // Try cache first
  const cached = getFromCache(cacheKey, config.ttl, config.version);
  if (cached) {
    console.log(`Using cached product for variant: ${variantId}`);
    return cached;
  }

  try {
    console.log(`Fetching single product for variant: ${variantId}`);
    
    // Fetch all products and find the one containing this variant
    const allProducts = await fetchProducts();
    const product = allProducts.find((p: any) => 
      p.variants.some((v: any) => v.id === variantId)
    );
    
    if (product) {
      // Cache this specific product
      setCache(cacheKey, product, config.version);
      return product;
    }
    
    throw new Error(`Product not found for variant: ${variantId}`);
  } catch (error) {
    console.error(`Error fetching single product for variant ${variantId}:`, error);
    throw error;
  }
}

// Preload products in background
export function preloadProducts() {
  // Start loading lightweight products in background
  fetchProductsLight().catch(error => {
    console.warn('Background preload failed:', error);
  });
}

// Force cache invalidation (call this when admin updates products)
export function invalidateProductsCache() {
  const keys = [
    CACHE_CONFIG.PRODUCTS_LIGHT.key,
    CACHE_CONFIG.PRODUCTS_FULL.key
  ];
  
  keys.forEach(key => {
    localStorage.removeItem(key);
  });
  
  // Clear all single product caches
  for (let i = localStorage.length - 1; i >= 0; i--) {
    const key = localStorage.key(i);
    if (key && key.startsWith(CACHE_CONFIG.SINGLE_PRODUCT.key)) {
      localStorage.removeItem(key);
    }
  }
  
  console.log('Products cache invalidated');
}

// Check if cache is stale and needs refresh
export function shouldRefreshCache(): boolean {
  const config = CACHE_CONFIG.PRODUCTS_LIGHT;
  const cacheKey = getCacheKey(config.key);
  const cached = getFromCache(cacheKey, config.ttl, config.version);
  return !cached;
}